import React, { useState } from 'react'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { AiFillEye, AiOutlineDelete, AiOutlineFileAdd } from 'react-icons/ai'
import { apiDelete } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import { DetailItem, CloseButton } from './TrainingShared.js'
import {
    formatDate,
    progressLabel,
    progressClass,
    materialSummary,
    countByStatus,
    materialKindLabel,
    materialKindsLabel,
    badgeBase
} from './trainingHelpers.js'

const FILE_HOST = process.env.REACT_APP_AWS_MEDIA_URL

function mediaUrl(value) {
    if (!value) return ''
    const text = String(value)
    if (text.includes('amazonaws.com/')) {
        const key = text.split('amazonaws.com/')[1] || ''
        return `${FILE_HOST || ''}${key.replace(/^\/+/, '')}`
    }
    if (text.startsWith('http://') || text.startsWith('https://')) return text
    return `${FILE_HOST || ''}${text.replace(/^\/+/, '')}`
}

function materialHref(material) {
    if (!material) return ''
    if (material.kind === 'link') return material.linkUrl || ''
    return mediaUrl(material.fileName)
}

function ProgressCell({ item }) {
    const summary = materialSummary(item)
    const showBar = summary.percent > 0 && summary.percent < 100
    return (
        <div className='min-w-[150px]'>
            <div className='flex items-center gap-2 flex-wrap'>
                <span className={`${badgeBase} ${progressClass(item.status)}`}>{progressLabel(item.status)}</span>
                {summary.total > 0 && (
                    <span className='text-xs text-gray-500'>{summary.done}/{summary.total} completed</span>
                )}
            </div>
            {showBar && (
                <div className='mt-2 flex items-center gap-2'>
                    <div className='h-1.5 flex-1 rounded-full bg-gray-200 overflow-hidden'>
                        <div className='h-full bg-blue-500' style={{ width: `${summary.percent}%` }} />
                    </div>
                    <span className='text-xs text-gray-500'>{summary.percent}% watched</span>
                </div>
            )}
        </div>
    )
}

function MaterialPreview({ material, index }) {
    const href = materialHref(material)
    const isImage = String(material?.fileType || '').startsWith('image/')

    return (
        <div className='rounded-lg border bg-white p-4 space-y-3'>
            <div className='flex items-center justify-between'>
                <p className='text-sm font-semibold text-slate-900'>
                    {index + 1}. {materialKindLabel(material?.kind)}
                </p>
                <span className={`${badgeBase} ${material?.completed ? progressClass('completed') : progressClass('assigned')}`}>
                    {material?.completed ? 'Completed' : 'Pending'}
                </span>
            </div>

            {material?.kind === 'link' && (
                <a className='text-blue-600 text-sm break-all underline' href={material.linkUrl} target='_blank' rel='noreferrer'>
                    {material.linkUrl}
                </a>
            )}

            {material?.kind === 'video' && href && (
                <>
                    <video controls className='w-full max-h-[300px] rounded border bg-black' src={href} />
                    <p className='text-xs text-gray-500'>Watched {material.progressPercent || 0}%</p>
                </>
            )}

            {material?.kind === 'pdf' && href && isImage && (
                <img src={href} alt={material.originalName || 'Assigned file'} className='max-h-[300px] rounded border' />
            )}

            {material?.kind === 'pdf' && href && !isImage && (
                <iframe title={material.originalName || 'Assigned file'} src={href} className='w-full h-[300px] rounded border' />
            )}

            {material?.originalName && material?.kind !== 'link' && (
                <p className='text-xs text-gray-500 break-all'>File: {material.originalName}</p>
            )}
        </div>
    )
}

function AssignmentDetails({ item, onClose }) {
    const materials = item?.materials || []
    const summary = materialSummary(item)
    const recurrence = item?.recurrence && item.recurrence !== 'none' ? item.recurrence : 'None'

    return (
        <div className='fixed inset-0 z-[60] flex items-center justify-center p-4'>
            <button type='button' className='absolute inset-0 bg-black/50' aria-label='Close details' onClick={onClose} />
            <div className='relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col'>
                <div className='flex items-start justify-between p-6 border-b shrink-0'>
                    <div>
                        <h3 className='text-lg font-semibold text-slate-900'>{item?.title || 'Assessment'}</h3>
                        <p className='text-sm text-gray-500 mt-1'>Assessment details</p>
                    </div>
                    <CloseButton onClick={onClose} />
                </div>

                <div className='flex-1 overflow-y-auto p-6 space-y-6'>
                    <section className='grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-lg border bg-gray-50 p-4'>
                        <div>
                            <p className='text-xs uppercase tracking-wide text-gray-500'>Status</p>
                            <span className={`mt-1 ${badgeBase} ${progressClass(item?.status)}`}>{progressLabel(item?.status)}</span>
                        </div>
                        <DetailItem label='Due date' value={formatDate(item?.dueDate)} />
                        {/* <DetailItem label='Recurrence' value={recurrence.charAt(0).toUpperCase() + recurrence.slice(1)} /> */}
                        <DetailItem
                            label='Progress'
                            value={summary.total ? `${summary.done}/${summary.total} completed` : '—'}
                        />
                        <div className='col-span-2 sm:col-span-4'>
                            <DetailItem label='Purpose' value={item?.description} />
                        </div>
                    </section>

                    <section>
                        <h4 className='text-sm font-semibold text-slate-900 mb-3'>
                            Materials <span className='font-normal text-gray-500'>({materials.length})</span>
                        </h4>
                        <div className='space-y-3'>
                            {materials.map((m, idx) => (
                                <MaterialPreview key={m._id || idx} material={m} index={idx} />
                            ))}
                            {!materials.length && <p className='text-sm text-gray-500'>No materials attached.</p>}
                        </div>
                    </section>
                </div>

                <div className='flex justify-end p-4 border-t shrink-0'>
                    <button type='button' className='text-black bg-[#E1E1E1] font-normal px-10 py-2.5 text-sm rounded' onClick={onClose}>
                        Back
                    </button>
                </div>
            </div>
        </div>
    )
}

// Modal opened by the eye icon: employee info, assessment summary, open details, remove.
function ViewAssessments({ employee, onClose, onAssign, onRemoved }) {
    const [details, setDetails] = useState(null)
    const assignments = employee?.assignments || []
    const counts = countByStatus(assignments)

    const removeAssignment = async (item) => {
        if (!item?._id) return
        const confirmed = await Swal.fire({
            title: 'Remove this assessment?',
            text: item.title || 'This assignment will be removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33'
        })
        if (!confirmed.isConfirmed) return
        try {
            const result = await apiDelete(`${apiPath.trainingAssignments}/${item._id}`)
            if (!result?.data?.success) {
                Swal.fire({ icon: 'error', title: result?.data?.message || 'Could not remove assessment' })
                return
            }
            if (details?._id === item._id) setDetails(null)
            if (onRemoved) onRemoved(item._id)
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: error?.response?.data?.message || 'Could not remove assessment'
            })
        }
    }

    return (
        <>
            <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
                <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col'>
                    <div className='flex items-start justify-between p-6 border-b shrink-0'>
                        <div>
                            <h3 className='text-lg font-semibold text-slate-900'>
                                {employee?.name || 'Employee'}{employee?.code ? ` (${employee.code})` : ''}
                            </h3>
                            <p className='text-sm text-gray-500 mt-1'>Training assessments</p>
                        </div>
                        <CloseButton onClick={onClose} />
                    </div>

                    <div className='flex-1 overflow-y-auto p-6 space-y-6'>
                        <section>
                            <h4 className='text-sm font-semibold text-slate-900 mb-3'>Employee details</h4>
                            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 rounded-lg border bg-gray-50 p-4'>
                                <DetailItem label='Department' value={employee?.department} />
                                <DetailItem label='Job title' value={employee?.job} />
                                <DetailItem label='Email' value={employee?.email} />
                                <DetailItem
                                    label='Hire date'
                                    value={employee?.hireDate ? dayjs(employee.hireDate).format('MM-DD-YYYY h:mm A') : 'N/A'}
                                />
                            </div>
                        </section>

                        <section className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                            {[
                                { label: 'Total', value: assignments.length, cls: 'text-slate-900' },
                                { label: 'Assigned', value: counts.assigned, cls: 'text-slate-700' },
                                { label: 'In progress', value: counts.in_progress, cls: 'text-blue-700' },
                                { label: 'Completed', value: counts.completed, cls: 'text-green-700' }
                            ].map((stat) => (
                                <div key={stat.label} className='rounded-lg border p-3 text-center'>
                                    <p className={`text-2xl font-semibold ${stat.cls}`}>{stat.value}</p>
                                    <p className='text-xs text-gray-500 mt-0.5'>{stat.label}</p>
                                </div>
                            ))}
                        </section>

                        <section>
                            <div className='flex items-center justify-between mb-3'>
                                <h4 className='text-sm font-semibold text-slate-900'>Assigned assessments</h4>
                                {onAssign && (
                                    <button
                                        type='button'
                                        className='inline-flex items-center gap-1.5 rounded bg-[#2f5bff] px-3 py-1.5 text-xs text-white hover:bg-blue-700'
                                        onClick={onAssign}
                                    >
                                        <AiOutlineFileAdd className='w-4 h-4' />
                                        Assign new
                                    </button>
                                )}
                            </div>

                            {assignments.length ? (
                                <div className='overflow-x-auto rounded-lg border'>
                                    <table className='w-full text-sm text-left text-gray-700'>
                                        <thead className='bg-[#E1E6EE] text-gray-900'>
                                            <tr>
                                                <th className='py-3 px-4 whitespace-nowrap'>Sr. No.</th>
                                                <th className='py-3 px-4'>Title</th>
                                                <th className='py-3 px-4'>Materials</th>
                                                <th className='py-3 px-4 whitespace-nowrap'>Due date</th>
                                                <th className='py-3 px-4'>Progress</th>
                                                <th className='py-3 px-4 text-center'>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignments.map((item, index) => (
                                                <tr key={item._id} className='border-b hover:bg-gray-50'>
                                                    <td className='py-3 px-4 font-medium text-gray-900'>{index + 1}</td>
                                                    <td className='py-3 px-4 text-gray-900'>{item.title || '—'}</td>
                                                    <td className='py-3 px-4'>{materialKindsLabel(item)}</td>
                                                    <td className='py-3 px-4 whitespace-nowrap'>{formatDate(item.dueDate)}</td>
                                                    <td className='py-3 px-4'><ProgressCell item={item} /></td>
                                                    <td className='py-3 px-4'>
                                                        <div className='flex items-center justify-center gap-3'>
                                                            <button
                                                                type='button'
                                                                className='text-[#2f5bff] hover:text-blue-800'
                                                                title='Open details'
                                                                aria-label='Open details'
                                                                onClick={() => setDetails(item)}
                                                            >
                                                                <AiFillEye className='w-5 h-5' />
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='text-red-600 hover:text-red-800'
                                                                title='Remove assessment'
                                                                aria-label='Remove assessment'
                                                                onClick={() => removeAssignment(item)}
                                                            >
                                                                <AiOutlineDelete className='w-5 h-5' />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className='rounded-lg border border-dashed p-6 text-center text-sm text-gray-500'>
                                    No assessment assigned yet.
                                </p>
                            )}
                        </section>
                    </div>
                </div>
            </div>

            {details && <AssignmentDetails item={details} onClose={() => setDetails(null)} />}
        </>
    )
}

export default ViewAssessments